/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * Full-Featured Property Manager Dashboard
 * White-Label Solution for Property Management Companies
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, TrendingUp, LogOut, Home, Briefcase, Users, DollarSign,
  PlusCircle, Settings, Eye, Calendar, FileText, Wrench, Bell,
  BarChart3, Palmtree, Globe, CreditCard, Percent, CheckCircle,
  ArrowUpRight, ArrowDownRight, RefreshCw, MapPin, Key, Clock,
  Search, Filter, MoreVertical, Mail, Phone, Edit, Trash2,
  Download, Upload, AlertCircle, CheckCircle2, XCircle, Paintbrush, Wallet, Calculator, Activity,
  HelpCircle
} from 'lucide-react';
import PayoutsDashboard from './PayoutsDashboard';
import PLReports from './PLReports';
import FinancialAnalytics from './FinancialAnalytics';
import VendorInvoiceReview from './VendorInvoiceReview';
import StaffManagement from './shared/StaffManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyManagerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [stats, setStats] = useState({
    totalProperties: 12,
    totalUnits: 156,
    occupancyRate: 87,
    monthlyRevenue: 68800,
    pendingMaintenance: 8,
    upcomingLeases: 5,
    totalTenants: 134,
    outstandingRent: 12400
  });
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [showAddModal, setShowAddModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Demo data
  const demoProperties = [
    { id: 1, name: 'Sunset Apartments', units: 24, occupied: 22, revenue: 12500, address: 'Nassau, Bahamas', type: 'Apartment Complex' },
    { id: 2, name: 'Palm Beach Condos', units: 16, occupied: 14, revenue: 9800, address: 'Paradise Island, Bahamas', type: 'Condominium' },
    { id: 3, name: 'Ocean View Tower', units: 48, occupied: 42, revenue: 28000, address: 'Cable Beach, Bahamas', type: 'High-Rise' },
    { id: 4, name: 'Marina Heights', units: 32, occupied: 28, revenue: 18500, address: 'Atlantis, Bahamas', type: 'Mixed-Use' },
  ];

  const demoTenants = [
    { id: 1, name: 'John Smith', email: 'john.smith@email.com', phone: '+1 242-555-0101', property: 'Sunset Apartments', unit: '4B', rent: 1200, status: 'current', balance: 0 },
    { id: 2, name: 'Sarah Johnson', email: 'sarah.j@email.com', phone: '+1 242-555-0102', property: 'Palm Beach Condos', unit: '12A', rent: 1800, status: 'current', balance: 0 },
    { id: 3, name: 'Michael Brown', email: 'm.brown@email.com', phone: '+1 242-555-0103', property: 'Ocean View Tower', unit: '22C', rent: 2200, status: 'overdue', balance: 2200 },
    { id: 4, name: 'Emily Davis', email: 'emily.d@email.com', phone: '+1 242-555-0104', property: 'Marina Heights', unit: '8D', rent: 1500, status: 'current', balance: 0 },
    { id: 5, name: 'David Wilson', email: 'd.wilson@email.com', phone: '+1 242-555-0105', property: 'Sunset Apartments', unit: '2A', rent: 1100, status: 'overdue', balance: 3300 },
    { id: 6, name: 'Lisa Anderson', email: 'lisa.a@email.com', phone: '+1 242-555-0106', property: 'Ocean View Tower', unit: '15B', rent: 2400, status: 'current', balance: 0 },
  ];

  const demoMaintenance = [
    { id: 1, property: 'Sunset Apartments', unit: '4B', issue: 'AC not cooling', priority: 'high', status: 'in_progress', tenant: 'John Smith', created: '2025-01-02', assigned: 'Cool Air Services' },
    { id: 2, property: 'Palm Beach Condos', unit: '12A', issue: 'Leaking faucet', priority: 'medium', status: 'pending', tenant: 'Sarah Johnson', created: '2025-01-03', assigned: null },
    { id: 3, property: 'Ocean View Tower', unit: '22C', issue: 'Broken window lock', priority: 'low', status: 'completed', tenant: 'Michael Brown', created: '2024-12-28', assigned: 'Island Repairs' },
    { id: 4, property: 'Marina Heights', unit: '8D', issue: 'Water heater malfunction', priority: 'high', status: 'pending', tenant: 'Emily Davis', created: '2025-01-04', assigned: null },
    { id: 5, property: 'Sunset Apartments', unit: '6C', issue: 'Electrical outlet sparking', priority: 'urgent', status: 'in_progress', tenant: 'Robert Lee', created: '2025-01-04', assigned: 'Quick Electric' },
  ];

  useEffect(() => {
    // Fetch organization info
    const fetchOrganization = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const response = await axios.get(`${BACKEND_URL}/api/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.organization) {
            setOrganization(response.data.organization);
          }
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
      }
    };
    
    fetchOrganization();
    
    // Simulate loading demo data
    setTimeout(() => {
      setProperties(demoProperties);
      setTenants(demoTenants);
      setMaintenanceRequests(demoMaintenance);
      setLoading(false);
    }, 1000);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-white',
      low: 'bg-blue-500 text-white'
    };
    return <Badge className={config[priority]}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      in_progress: { class: 'bg-blue-100 text-blue-700', icon: RefreshCw },
      completed: { class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      current: { class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      overdue: { class: 'bg-red-100 text-red-700', icon: AlertCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.class}>
        <Icon className="h-3 w-3 mr-1" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Estate Flow</h1>
                <p className="text-sm text-gray-500">Property Manager Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/vacation-rentals')}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <Palmtree className="mr-2 h-4 w-4" />
                Vacation Rentals
              </Button>
              <Button variant="outline" size="sm">
                <Paintbrush className="mr-2 h-4 w-4" />
                White-Label Settings
              </Button>
              <Button 
                onClick={() => navigate('/kb')} 
                variant="ghost"
                data-testid="pm-dashboard-help-btn"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                Help
              </Button>
              <Button onClick={handleLogout} variant="outline">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Properties</CardTitle>
              <Building2 className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalProperties}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Units</CardTitle>
              <Key className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUnits}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Tenants</CardTitle>
              <Users className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTenants}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Occupancy</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.occupancyRate}%</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">${(stats.monthlyRevenue/1000).toFixed(0)}k</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Outstanding</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">${(stats.outstandingRent/1000).toFixed(1)}k</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Maintenance</CardTitle>
              <Wrench className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.pendingMaintenance}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium">Leases</CardTitle>
              <FileText className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.upcomingLeases}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="properties">Properties</TabsTrigger>
            <TabsTrigger value="tenants">Tenants</TabsTrigger>
            <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
            <TabsTrigger value="financials">Financials</TabsTrigger>
            <TabsTrigger value="analytics" className="text-indigo-600">📈 Analytics</TabsTrigger>
            <TabsTrigger value="tax-pl" className="text-purple-600">📊 Tax & P&L</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
            <TabsTrigger value="whitelabel">White-Label</TabsTrigger>
            <TabsTrigger value="staff" className="text-red-600">Staff</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20 flex-col" onClick={() => setShowAddModal('property')}>
                    <PlusCircle className="h-6 w-6 mb-2 text-purple-600" />
                    Add Property
                  </Button>
                  <Button variant="outline" className="h-20 flex-col" onClick={() => setShowAddModal('tenant')}>
                    <Users className="h-6 w-6 mb-2 text-blue-600" />
                    Add Tenant
                  </Button>
                  <Button variant="outline" className="h-20 flex-col" onClick={() => setShowAddModal('maintenance')}>
                    <Wrench className="h-6 w-6 mb-2 text-orange-600" />
                    New Request
                  </Button>
                  <Button variant="outline" className="h-20 flex-col">
                    <DollarSign className="h-6 w-6 mb-2 text-emerald-600" />
                    Record Payment
                  </Button>
                </CardContent>
              </Card>

              {/* Vacation Rentals Promo */}
              <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palmtree className="h-5 w-5 text-emerald-600" />
                    Vacation Rentals Management
                  </CardTitle>
                  <CardDescription>
                    Expand your portfolio with short-term rentals
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span>Channel sync: Expedia, Booking.com, VRBO</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span>Book Direct & Save widget</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span>Only 2% platform fee</span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/vacation-rentals')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Palmtree className="mr-2 h-4 w-4" />
                    Go to Vacation Rentals
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity & Maintenance */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { icon: DollarSign, text: 'Rent payment received - Unit 4B', time: '2 hours ago', color: 'text-emerald-600' },
                      { icon: Wrench, text: 'Maintenance completed - Unit 2A', time: '5 hours ago', color: 'text-blue-600' },
                      { icon: Users, text: 'New tenant application - Unit 7C', time: '1 day ago', color: 'text-purple-600' },
                      { icon: FileText, text: 'Lease renewed - Unit 1A', time: '2 days ago', color: 'text-green-600' },
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <item.icon className={`h-5 w-5 ${item.color}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.text}</p>
                          <p className="text-xs text-gray-500">{item.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Urgent Maintenance</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('maintenance')}>View All</Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {maintenanceRequests.filter(m => m.priority === 'urgent' || m.priority === 'high').slice(0, 4).map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{req.issue}</p>
                          <p className="text-xs text-gray-500">{req.property} - {req.unit}</p>
                        </div>
                        <div className="flex gap-2">
                          {getPriorityBadge(req.priority)}
                          {getStatusBadge(req.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Managed Properties ({properties.length})</h2>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    placeholder="Search properties..." 
                    className="pl-10 w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowAddModal('property')}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Property
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <Card key={property.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{property.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {property.address}
                        </CardDescription>
                      </div>
                      <Badge variant="outline">{property.type}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-2xl font-bold">{property.units}</p>
                        <p className="text-xs text-gray-500">Total Units</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">
                          {Math.round((property.occupied / property.units) * 100)}%
                        </p>
                        <p className="text-xs text-gray-500">Occupancy</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <p className="text-lg font-bold text-emerald-600">${property.revenue.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Monthly Revenue</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Tenants Tab */}
          <TabsContent value="tenants" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">All Tenants ({tenants.length})</h2>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input placeholder="Search tenants..." className="pl-10 w-64" />
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowAddModal('tenant')}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Tenant
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-4 font-medium">Tenant</th>
                        <th className="text-left p-4 font-medium">Property</th>
                        <th className="text-left p-4 font-medium">Unit</th>
                        <th className="text-left p-4 font-medium">Rent</th>
                        <th className="text-left p-4 font-medium">Balance</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map((tenant) => (
                        <tr key={tenant.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{tenant.name}</p>
                              <p className="text-sm text-gray-500">{tenant.email}</p>
                            </div>
                          </td>
                          <td className="p-4">{tenant.property}</td>
                          <td className="p-4">{tenant.unit}</td>
                          <td className="p-4 font-medium">${tenant.rent.toLocaleString()}</td>
                          <td className="p-4">
                            <span className={tenant.balance > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                              ${tenant.balance.toLocaleString()}
                            </span>
                          </td>
                          <td className="p-4">{getStatusBadge(tenant.status)}</td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm"><Mail className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm"><Phone className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Maintenance Requests ({maintenanceRequests.length})</h2>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
                <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setShowAddModal('maintenance')}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  New Request
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-4 font-medium">Issue</th>
                        <th className="text-left p-4 font-medium">Property</th>
                        <th className="text-left p-4 font-medium">Tenant</th>
                        <th className="text-left p-4 font-medium">Priority</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Assigned</th>
                        <th className="text-left p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maintenanceRequests.map((req) => (
                        <tr key={req.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <p className="font-medium">{req.issue}</p>
                            <p className="text-xs text-gray-500">Created: {req.created}</p>
                          </td>
                          <td className="p-4">
                            <p>{req.property}</p>
                            <p className="text-sm text-gray-500">Unit {req.unit}</p>
                          </td>
                          <td className="p-4">{req.tenant}</td>
                          <td className="p-4">{getPriorityBadge(req.priority)}</td>
                          <td className="p-4">{getStatusBadge(req.status)}</td>
                          <td className="p-4">{req.assigned || <span className="text-gray-400">Unassigned</span>}</td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm">Assign</Button>
                              <Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financials Tab */}
          <TabsContent value="financials" className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-emerald-700">Total Revenue (MTD)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-emerald-600">${stats.monthlyRevenue.toLocaleString()}</p>
                  <p className="text-sm text-emerald-600 flex items-center mt-1">
                    <ArrowUpRight className="h-4 w-4 mr-1" />
                    +12.5% from last month
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-red-50 border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-red-700">Outstanding Rent</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-red-600">${stats.outstandingRent.toLocaleString()}</p>
                  <p className="text-sm text-red-600">5 tenants overdue</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-blue-700">Expenses (MTD)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-600">$8,450</p>
                  <p className="text-sm text-blue-600">Maintenance & utilities</p>
                </CardContent>
              </Card>
              
              <Card className="bg-purple-50 border-purple-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-purple-700">Net Income</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-purple-600">${(stats.monthlyRevenue - 8450).toLocaleString()}</p>
                  <p className="text-sm text-purple-600">After expenses</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Revenue by Property</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {properties.map((prop) => (
                      <div key={prop.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-purple-600" />
                          <span className="font-medium">{prop.name}</span>
                        </div>
                        <span className="font-bold text-emerald-600">${prop.revenue.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { type: 'income', desc: 'Rent - Unit 4B', amount: 1200, date: 'Jan 4' },
                      { type: 'income', desc: 'Rent - Unit 12A', amount: 1800, date: 'Jan 3' },
                      { type: 'expense', desc: 'AC Repair - Unit 4B', amount: -450, date: 'Jan 3' },
                      { type: 'income', desc: 'Rent - Unit 22C', amount: 2200, date: 'Jan 2' },
                      { type: 'expense', desc: 'Plumbing - Unit 8D', amount: -180, date: 'Jan 1' },
                    ].map((tx, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-3">
                          {tx.type === 'income' ? (
                            <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{tx.desc}</p>
                            <p className="text-xs text-gray-500">{tx.date}</p>
                          </div>
                        </div>
                        <span className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <FinancialAnalytics 
              orgId={organization?.id} 
              orgName={organization?.name || "Property Management Company"}
              orgType="property_manager"
            />
            <div className="mt-6">
              <VendorInvoiceReview />
            </div>
          </TabsContent>

          {/* Tax & P&L Tab */}
          <TabsContent value="tax-pl" className="space-y-6">
            <PLReports 
              orgId={organization?.id} 
              orgName={organization?.name || "Property Management Company"}
              orgCountry={organization?.country || "US"}
            />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Reports & Analytics</h2>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export All
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Rent Roll Report', desc: 'Complete tenant and rent summary', icon: FileText, color: 'blue' },
                { title: 'Financial Summary', desc: 'Income, expenses, and net profit', icon: DollarSign, color: 'emerald' },
                { title: 'Occupancy Report', desc: 'Vacancy rates and trends', icon: BarChart3, color: 'purple' },
                { title: 'Maintenance Report', desc: 'Request history and costs', icon: Wrench, color: 'orange' },
                { title: 'Lease Expiration', desc: 'Upcoming lease renewals', icon: Calendar, color: 'red' },
                { title: 'Owner Statement', desc: 'Property owner distributions', icon: Briefcase, color: 'indigo' },
              ].map((report, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className={`inline-flex p-3 rounded-lg bg-${report.color}-100 mb-4`}>
                      <report.icon className={`h-6 w-6 text-${report.color}-600`} />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{report.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">{report.desc}</p>
                    <Button variant="outline" className="w-full">
                      <Download className="mr-2 h-4 w-4" />
                      Generate
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" className="space-y-6">
            <PayoutsDashboard ownerId="demo_owner" />
          </TabsContent>

          {/* White-Label Tab */}
          <TabsContent value="whitelabel" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Paintbrush className="h-5 w-5" />
                  White-Label Settings
                </CardTitle>
                <CardDescription>
                  Customize the platform with your company branding for your clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold">Company Branding</h3>
                    <div>
                      <label className="block text-sm font-medium mb-2">Company Name</label>
                      <Input placeholder="Your Company Name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Logo URL</label>
                      <Input placeholder="https://yourcompany.com/logo.png" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Primary Color</label>
                      <div className="flex gap-2">
                        <Input type="color" className="w-16 h-10" defaultValue="#7C3AED" />
                        <Input placeholder="#7C3AED" defaultValue="#7C3AED" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Support Email</label>
                      <Input placeholder="support@yourcompany.com" />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-semibold">Custom Domain</h3>
                    <div>
                      <label className="block text-sm font-medium mb-2">Custom Domain</label>
                      <Input placeholder="app.yourcompany.com" />
                      <p className="text-xs text-gray-500 mt-1">Point your domain to our servers</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Favicon URL</label>
                      <Input placeholder="https://yourcompany.com/favicon.ico" />
                    </div>
                    
                    <div className="pt-4">
                      <h4 className="font-medium mb-2">Features Included:</h4>
                      <div className="space-y-2">
                        {[
                          'Custom branding on all pages',
                          'White-label tenant portals',
                          'Custom email templates',
                          'Branded payment pages',
                          'Remove Estate Flow branding',
                          'Custom domain support'
                        ].map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline">Reset</Button>
                  <Button className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="staff" className="space-y-6">
            <StaffManagement />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PropertyManagerDashboard;